import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { EmptyYacht } from "./Inventory";
import { Image as ImageIcon, Trash2, Pencil, X, Play, Plus, FolderOpen } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import FileUploader, { AuthImage, AuthVideo, VideoPosterBg } from "../../components/FileUploader";
import { isVideoPath } from "../../lib/videoCompress";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";

export default function Photos() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can } = useRights();
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [openPhoto, setOpenPhoto] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [albumDialog, setAlbumDialog] = useState(null); // {mode:'new'|'rename', name}
  const [albumDeleteOpen, setAlbumDeleteOpen] = useState(false);
  const [alsoDeleteMedia, setAlsoDeleteMedia] = useState(false);
  const [albumBusy, setAlbumBusy] = useState(false);

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/photos?yacht_id=${activeYacht.id}`);
      setItems(data);
    } catch {
      setItems([]);
    }
    try {
      const { data } = await api.get(`/photo-albums?yacht_id=${activeYacht.id}`);
      setAlbums(data);
    } catch {
      setAlbums([]);
    }
  };
  useEffect(() => { setItems([]); setAlbums([]); setActiveTag(null); setActiveAlbum(null); load(); /* eslint-disable-next-line */ }, [activeYacht?.id]);

  // New upload → create the record bare (inside the open album, if any), then
  // open the media straight in the edit view so the user fills caption/tags
  // and Saves (or cancels).
  const onUploaded = async (path) => {
    try {
      const { data } = await api.post("/photos", {
        yacht_id: activeYacht.id,
        file_path: path,
        caption: "",
        tags: [],
        album_ids: activeAlbum ? [activeAlbum] : [],
      });
      await load();
      setOpenPhoto({ ...data, _startEditing: true });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Album scope applied first; tag chips then work WITHIN the open album.
  const albumItems = activeAlbum
    ? items.filter((p) => (p.album_ids || []).includes(activeAlbum))
    : items;

  // Tag chip row above the grid. Each unique tag carries its photo count so
  // crew can scan "exterior · 12  engine room · 4  provisioning · 7" and tap
  // one to drill in. Tag matching is case-insensitive but display preserves
  // the original casing of the first occurrence.
  const tagSummary = (() => {
    const seen = new Map(); // key: lowercase, value: { label, count }
    for (const p of albumItems) {
      for (const t of p.tags || []) {
        const k = String(t).trim().toLowerCase();
        if (!k) continue;
        const cur = seen.get(k);
        if (cur) { cur.count += 1; }
        else { seen.set(k, { key: k, label: String(t).trim(), count: 1 }); }
      }
    }
    return Array.from(seen.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  })();

  const filteredItems = activeTag
    ? albumItems.filter((p) => (p.tags || []).some((t) => String(t).trim().toLowerCase() === activeTag))
    : albumItems;

  // All existing tags, alphabetical — offered as one-tap chips in the edit
  // view so crew never retype a tag that already exists.
  const allTags = [...tagSummary].sort((a, b) => a.label.localeCompare(b.label)).map((t) => t.label);

  const albumCount = (aid) => items.filter((p) => (p.album_ids || []).includes(aid)).length;
  const albumCover = (aid) => items.find((p) => (p.album_ids || []).includes(aid))?.file_path || null;
  const activeAlbumObj = albums.find((a) => a.id === activeAlbum) || null;

  const saveAlbum = async () => {
    const name = (albumDialog?.name || "").trim();
    if (!name || (albumDialog.mode === "rename" && !activeAlbum)) return;
    setAlbumBusy(true);
    try {
      if (albumDialog.mode === "rename") {
        const { data } = await api.patch(`/photo-albums/${activeAlbum}`, { name });
        setAlbums((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        toast.success(t("photos.album_renamed"));
      } else {
        const { data } = await api.post("/photo-albums", { yacht_id: activeYacht.id, name });
        setAlbums((prev) => [...prev, data]);
        setActiveAlbum(data.id);
        setActiveTag(null);
        toast.success(t("photos.album_created"));
      }
      setAlbumDialog(null);
    } catch (e) { toast.error(fmtErr(e)); } finally { setAlbumBusy(false); }
  };

  const removeAlbum = async () => {
    if (!activeAlbum) return;
    setAlbumBusy(true);
    try {
      await api.delete(`/photo-albums/${activeAlbum}?delete_media=${alsoDeleteMedia}`);
      toast.success(t("photos.album_deleted"));
      setAlbumDeleteOpen(false);
      setAlsoDeleteMedia(false);
      setActiveAlbum(null);
      setActiveTag(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setAlbumBusy(false); }
  };

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("photos.title")}
        right={can("photo:create") ? <TourReplayButton tourId="media" /> : null}
      />

      {can("photo:create") && (
      <div className="mt-8 rounded-md border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-medium">{t("photos.upload_title")}</div>
          <FileUploader
            yacht_id={activeYacht.id}
            folder="photos"
            accept="image/*,video/*"
            enableVideo
            multiple
            onUploaded={onUploaded}
            label={t("photos.upload_button")}
            testid="photo-upload"
          />
        </div>
      </div>
      )}

      {/* ── Albums — group media per trip / refit / event ─────────────── */}
      {(albums.length > 0 || can("photo:create")) && (
        <div className="mt-4" data-testid="album-bar">
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5">
            {t("photos.albums_title")}
          </div>
          <div className="flex items-stretch gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => { setActiveAlbum(null); setActiveTag(null); }}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                activeAlbum === null ? "bg-ink text-white border-ink" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid="album-all"
            >
              <ImageIcon className="h-4 w-4" />
              {t("photos.album_all")}
              <span className={activeAlbum === null ? "text-zinc-300 text-xs" : "text-zinc-400 text-xs"}>{items.length}</span>
            </button>
            {albums.map((a) => {
              const cover = albumCover(a.id);
              const selected = activeAlbum === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { setActiveAlbum(selected ? null : a.id); setActiveTag(null); }}
                  className={`flex items-center gap-2 rounded-lg border pl-1.5 pr-3 py-1.5 text-sm transition-colors ${
                    selected ? "bg-ink text-white border-ink" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`album-card-${a.id}`}
                >
                  <span className="h-8 w-8 rounded-md overflow-hidden bg-zinc-100 grid place-items-center shrink-0">
                    {cover ? (
                      isVideoPath(cover)
                        ? <VideoPosterBg path={cover} />
                        : <AuthImage path={cover} className="h-full w-full object-cover" alt="" />
                    ) : (
                      <FolderOpen className="h-4 w-4 text-zinc-400" />
                    )}
                  </span>
                  <span className="max-w-[140px] truncate">{a.name}</span>
                  <span className={selected ? "text-zinc-300 text-xs" : "text-zinc-400 text-xs"}>{albumCount(a.id)}</span>
                </button>
              );
            })}
            {can("photo:create") && (
              <button
                type="button"
                onClick={() => setAlbumDialog({ mode: "new", name: "" })}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 transition-colors"
                data-testid="album-new-btn"
              >
                <Plus className="h-4 w-4" /> {t("photos.new_album")}
              </button>
            )}
          </div>
          {activeAlbumObj && can("photo:delete") && (
            <div className="mt-1.5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setAlbumDialog({ mode: "rename", name: activeAlbumObj.name })}
                className="text-xs text-zinc-600 underline underline-offset-2 hover:text-black inline-flex items-center gap-1"
                data-testid="album-rename-btn"
              >
                <Pencil className="h-3 w-3" /> {t("photos.rename_album")}
              </button>
              <button
                type="button"
                onClick={() => { setAlsoDeleteMedia(false); setAlbumDeleteOpen(true); }}
                className="text-xs text-red-600 underline underline-offset-2 hover:text-red-700 inline-flex items-center gap-1"
                data-testid="album-delete-btn"
              >
                <Trash2 className="h-3 w-3" /> {t("photos.delete_album")}
              </button>
            </div>
          )}
        </div>
      )}

      {tagSummary.length > 0 && (
        <div className="mt-4 flex items-center gap-1.5 flex-wrap" data-testid="photo-tag-filter">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
              activeTag === null
                ? "bg-ink text-white border-ink"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
            }`}
            data-testid="photo-tag-all"
          >
            {t("common.all")}
            <span className={`ml-1.5 ${activeTag === null ? "text-zinc-300" : "text-zinc-400"}`}>{albumItems.length}</span>
          </button>
          {tagSummary.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTag((cur) => (cur === t.key ? null : t.key))}
              className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                activeTag === t.key
                  ? "bg-ink text-white border-ink"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`photo-tag-${t.key}`}
            >
              {t.label}
              <span className={`ml-1.5 ${activeTag === t.key ? "text-zinc-300" : "text-zinc-400"}`}>{t.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
        {filteredItems.length === 0 && (
          <div className="col-span-full rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500">
            <ImageIcon className="h-6 w-6 mx-auto text-zinc-300" />
            <div className="mt-2">
              {albumItems.length === 0
                ? (activeAlbum ? t("photos.no_photos_album") : t("photos.no_photos"))
                : t("photos.no_photos_filtered", { tag: tagSummary.find((tg) => tg.key === activeTag)?.label || activeTag })}
            </div>
            {albumItems.length > 0 && activeTag && (
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                className="mt-3 text-xs text-ink underline underline-offset-2"
              >
                {t("photos.clear_filter")}
              </button>
            )}
          </div>
        )}
        {filteredItems.map((p) => (
          <button
            type="button"
            key={p.id}
            onClick={() => setOpenPhoto(p)}
            className="text-left rounded-md border border-zinc-200 bg-white overflow-hidden group hover:border-zinc-400 hover:shadow-sm transition-all"
            data-testid={`photo-${p.id}`}
          >
            <div className="aspect-square">
              {isVideoPath(p.file_path) ? (
                <div className="relative w-full h-full grid place-items-center overflow-hidden bg-zinc-900" data-testid={`photo-video-${p.id}`}>
                  <VideoPosterBg path={p.file_path} />
                  <span className="absolute inset-0 bg-black/15" />
                  <span className="relative grid place-items-center h-10 w-10 rounded-full bg-white/90 text-zinc-900 shadow">
                    <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
                  </span>
                  <span className="absolute bottom-1 right-1 text-[9px] uppercase tracking-wider bg-black/70 text-white px-1 py-0.5 rounded">Video</span>
                </div>
              ) : (
                <AuthImage path={p.file_path} className="w-full h-full object-cover" alt={p.caption || ""} />
              )}
            </div>
            <div className="p-2">
              <div className="text-xs truncate" title={p.caption || ""}>{p.caption || "—"}</div>
              <div className="text-[10px] text-zinc-500 flex items-center justify-between gap-1">
                <span>{(p.created_at || "").slice(0, 10)}</span>
                {(p.tags || []).length > 0 && (
                  <span className="truncate text-zinc-400">{(p.tags || []).slice(0, 2).join(", ")}</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {openPhoto && (
        <PhotoLightbox
          photo={openPhoto}
          can={can}
          allTags={allTags}
          albums={albums}
          startEditing={!!openPhoto._startEditing}
          onClose={() => setOpenPhoto(null)}
          onSaved={(updated) => {
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
          }}
          onDeleted={() => {
            const id = openPhoto.id;
            setOpenPhoto(null);
            setItems((prev) => prev.filter((it) => it.id !== id));
          }}
        />
      )}

      {/* New / rename album */}
      {albumDialog && (
        <Dialog open onOpenChange={(o) => { if (!o && !albumBusy) setAlbumDialog(null); }}>
          <DialogContent className="max-w-sm" data-testid="album-dialog">
            <div className="text-sm font-medium">
              {albumDialog.mode === "rename" ? t("photos.rename_album") : t("photos.new_album")}
            </div>
            <Input
              autoFocus
              value={albumDialog.name}
              onChange={(e) => setAlbumDialog({ ...albumDialog, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveAlbum(); } }}
              placeholder={t("photos.album_name_placeholder")}
              maxLength={80}
              data-testid="album-name-input"
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAlbumDialog(null)} disabled={albumBusy}>
                {t("common.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={saveAlbum}
                disabled={albumBusy || !albumDialog.name.trim()}
                className="bg-ink hover:bg-black text-white"
                data-testid="album-save-btn"
              >
                {albumBusy ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete album — media only goes with it when explicitly ticked */}
      <AlertDialog open={albumDeleteOpen} onOpenChange={(o) => { if (!o && !albumBusy) setAlbumDeleteOpen(false); }}>
        <AlertDialogContent data-testid="album-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("photos.album_delete_title", { name: activeAlbumObj?.name || "" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("photos.album_delete_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          {albumCount(activeAlbum) > 0 && (
            <label className="flex items-start gap-2 text-sm text-zinc-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={alsoDeleteMedia}
                onChange={(e) => setAlsoDeleteMedia(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-red-600"
                data-testid="album-delete-media-checkbox"
              />
              <span>{t("photos.album_delete_media_label", { count: albumCount(activeAlbum) })}</span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={albumBusy} data-testid="album-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); removeAlbum(); }}
              disabled={albumBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="album-delete-confirm-btn"
            >
              {albumBusy ? t("photos.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Lightbox + inline edit ───────────────────────────────────────────
 * Tapping any photo opens this modal. It shows the full-resolution image
 * (proxied through /api/files which authenticates via the user's token),
 * the caption, tags and the upload date. Anyone holding `photo:edit` can
 * flip into edit mode to update the caption / comma-separated tags;
 * Captain+ (photo:delete) gets the destructive delete action. */
function PhotoLightbox({ photo, can, allTags = [], albums = [], startEditing = false, onClose, onSaved, onDeleted }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(startEditing);
  const [caption, setCaption] = useState(photo.caption || "");
  const [tags, setTags] = useState((photo.tags || []).join(", "));
  const [albumIds, setAlbumIds] = useState(photo.album_ids || []);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setEditing(startEditing);
    setCaption(photo.caption || "");
    setTags((photo.tags || []).join(", "));
    setAlbumIds(photo.album_ids || []);
  }, [photo.id, photo.caption, photo.tags, photo.album_ids, startEditing]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        caption: caption.trim(),
        tags: tags ? tags.split(",").map((tg) => tg.trim()).filter(Boolean) : [],
        album_ids: albumIds,
      };
      const { data } = await api.patch(`/photos/${photo.id}`, body);
      toast.success(t("photos.photo_updated"));
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/photos/${photo.id}`);
      toast.success(t("photos.photo_deleted"));
      setConfirmOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(fmtErr(e));
      setDeleting(false);
    }
  };

  // Current tags parsed from the comma-separated input.
  const currentTags = tags.split(",").map((tg) => tg.trim()).filter(Boolean);
  const hasTag = (label) => currentTags.some((tg) => tg.toLowerCase() === label.toLowerCase());
  const toggleTag = (label) => {
    const next = hasTag(label)
      ? currentTags.filter((tg) => tg.toLowerCase() !== label.toLowerCase())
      : [...currentTags, label];
    setTags(next.join(", "));
  };

  const toggleAlbum = (aid) => {
    setAlbumIds((prev) => (prev.includes(aid) ? prev.filter((x) => x !== aid) : [...prev, aid]));
  };
  const photoAlbums = albums.filter((a) => (photo.album_ids || []).includes(a.id));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden p-0 bg-zinc-950 border-zinc-800 text-zinc-100"
        data-testid="photo-lightbox"
      >
        {/* Close button — DialogContent already ships its own X but we
            recolor it via this absolute button so it's visible against
            the dark image background. */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5"
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] max-h-[92vh]">
          <div className="flex items-center justify-center bg-black min-h-[40vh] md:min-h-[60vh] overflow-hidden">
            {isVideoPath(photo.file_path) ? (
              <AuthVideo
                path={photo.file_path}
                className="max-h-[92vh] max-w-full w-auto h-auto object-contain bg-black"
                autoPlay
              />
            ) : (
              <AuthImage
                path={photo.file_path}
                alt={photo.caption || ""}
                className="max-h-[92vh] max-w-full w-auto h-auto object-contain"
              />
            )}
          </div>

          <div className="bg-white text-zinc-900 p-5 overflow-y-auto" data-testid="lightbox-meta">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">{t("photos.title")}</div>
            <div className="mt-1 text-[12px] text-zinc-500">
              {(photo.created_at || "").slice(0, 10)}
            </div>

            {!editing ? (
              <>
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_caption")}</div>
                  <div className="text-sm whitespace-pre-wrap" data-testid="lightbox-caption">
                    {photo.caption || <span className="text-zinc-400">{t("photos.lightbox_no_caption")}</span>}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_tags")}</div>
                  {(photo.tags || []).length === 0 ? (
                    <div className="text-sm text-zinc-400">{t("photos.lightbox_no_tags")}</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="lightbox-tags">
                      {(photo.tags || []).map((tg) => (
                        <span key={tg} className="text-[11px] bg-zinc-100 text-zinc-700 rounded-full px-2 py-0.5 border border-zinc-200">
                          {tg}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_albums")}</div>
                  {photoAlbums.length === 0 ? (
                    <div className="text-sm text-zinc-400">{t("photos.lightbox_no_albums")}</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="lightbox-albums">
                      {photoAlbums.map((a) => (
                        <span key={a.id} className="text-[11px] bg-zinc-100 text-zinc-700 rounded-full px-2 py-0.5 border border-zinc-200 inline-flex items-center gap-1">
                          <FolderOpen className="h-3 w-3 text-zinc-400" />{a.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center gap-2">
                  {can("photo:edit") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                      data-testid="lightbox-edit-btn"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("photos.edit_photo")}
                    </Button>
                  )}
                  {can("photo:delete") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmOpen(true)}
                      disabled={deleting}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="lightbox-delete-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {deleting ? t("photos.deleting") : t("photos.delete_photo")}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_caption")}</div>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder={t("photos.caption_input_placeholder")}
                    data-testid="lightbox-caption-input"
                  />
                </div>

                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_tags")}</div>
                  {allTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="lightbox-tag-options">
                      {allTags.map((label) => {
                        const selected = hasTag(label);
                        return (
                          <button
                            key={label.toLowerCase()}
                            type="button"
                            onClick={() => toggleTag(label)}
                            className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                              selected
                                ? "bg-ink text-white border-ink"
                                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                            }`}
                            data-testid={`lightbox-tag-option-${label.toLowerCase()}`}
                          >
                            {selected ? "✓ " : "+ "}{label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder={t("photos.tags_input_placeholder")}
                    data-testid="lightbox-tags-input"
                  />
                  <div className="text-[10px] text-zinc-500 mt-1">{t("photos.tags_input_help")}</div>
                </div>

                {albums.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{t("photos.lightbox_albums")}</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="lightbox-album-options">
                      {albums.map((a) => {
                        const selected = albumIds.includes(a.id);
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => toggleAlbum(a.id)}
                            className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                              selected
                                ? "bg-ink text-white border-ink"
                                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                            }`}
                            data-testid={`lightbox-album-option-${a.id}`}
                          >
                            {selected ? "✓ " : "+ "}{a.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-5 flex items-center gap-2">
                  <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-ink hover:bg-black text-white"
                    data-testid="lightbox-save-btn"
                  >
                    {saving ? t("common.saving") : t("common.save")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (startEditing) { onClose(); return; }
                      setEditing(false);
                      setCaption(photo.caption || "");
                      setTags((photo.tags || []).join(", "));
                      setAlbumIds(photo.album_ids || []);
                    }}
                    disabled={saving}
                    data-testid="lightbox-cancel-btn"
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!o && !deleting) setConfirmOpen(false); }}>
        <AlertDialogContent data-testid="photo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("photos.confirm_delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("common.delete_permanent_warning")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="photo-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="photo-delete-confirm-btn"
            >
              {deleting ? t("photos.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
